import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import "./AdminAddTabletennes.css";

/**
 * Hutumia Firebase Auth ikiwa ipo. Ikiwa Login.jsx ya app hii inahifadhi
 * hali ya login kwenye localStorage, tumia taarifa hizo pia.
 */
function getAppLoginUser() {
  if (auth.currentUser) {
    return {
      uid: auth.currentUser.uid,
      username: auth.currentUser.displayName || "",
      source: "firebase",
    };
  }

  try {
    if (localStorage.getItem("authenticated") !== "true") return null;

    let savedUser = {};
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      try {
        savedUser = JSON.parse(rawUser) || {};
      } catch {
        savedUser = {};
      }
    }

    const username = savedUser.username || localStorage.getItem("username") || "";
    return {
      ...savedUser,
      uid: savedUser.uid || savedUser.id || username,
      username,
      source: "localStorage",
    };
  } catch (error) {
    console.warn("Could not read app login state:", error);
    return null;
  }
}

const COLLECTION = "tabletennis";
const TIMEZONE = "Africa/Dar_es_Salaam";
const SETS = [1, 2, 3, 4, 5];

// Correct score odds za Best of 5 games.
// Hizi ni odds za mfano; admin anaweza kuzibadilisha.
const DEFAULT_SCORE_ODDS = [
  { score: "3-0", odds: 2.10 },
  { score: "3-1", odds: 3.20 },
  { score: "3-2", odds: 4.50 },
  { score: "0-3", odds: 2.30 },
  { score: "1-3", odds: 3.50 },
  { score: "2-3", odds: 4.80 },
];

const EMPTY_FORM = {
  country: "",
  league: "",
  homeTeam: "",
  awayTeam: "",
  date: "",
  time: "",
  homeOdds: "",
  awayOdds: "",
};

const EMPTY_SCORE = {
  homeSet1: "",
  awaySet1: "",
  homeSet2: "",
  awaySet2: "",
  homeSet3: "",
  awaySet3: "",
  homeSet4: "",
  awaySet4: "",
  homeSet5: "",
  awaySet5: "",
};

function makeDefaultScoreOdds() {
  return DEFAULT_SCORE_ODDS.map((item) => ({
    score: item.score,
    odds: String(item.odds),
  }));
}

function makeInitialScoreOdds(savedOdds) {
  const saved = Array.isArray(savedOdds) ? savedOdds : [];
  const savedMap = new Map(
    saved.map((item) => [item.score, item.odds])
  );

  return DEFAULT_SCORE_ODDS.map((item) => ({
    score: item.score,
    odds:
      savedMap.get(item.score) != null
        ? String(savedMap.get(item.score))
        : String(item.odds),
  }));
}

function cleanCell(value) {
  return String(value ?? "")
    .replace(/\*\*/g, "")
    .replace(/<\/?strong>/gi, "")
    .replace(/<\/?b>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u00a0/g, " ")
    .replace(/\\\|/g, "|")
    .replace(/\s+/g, " ")
    .trim();
}

function safeNumber(value) {
  if (value === "" || value == null) return null;

  const number = Number(String(value).replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
    return false;
  }

  const date = new Date(`${value}T12:00:00Z`);

  return (
    !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
  );
}

function getEATDate(offsetDays = 0) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = Number(parts.find((p) => p.type === "year").value);
  const month = Number(parts.find((p) => p.type === "month").value);
  const day = Number(parts.find((p) => p.type === "day").value);

  return new Date(Date.UTC(year, month - 1, day + offsetDays, 12))
    .toISOString()
    .slice(0, 10);
}

function removeMatchId(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .replace(/#\s*\d+\s*$/, "")
    .trim();
}

function normaliseTime(value) {
  const clean = removeMatchId(value)
    .replace(/^(Today|Tomorrow)\s+/i, "")
    .trim();

  const match = clean.match(
    /^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i
  );

  if (!match) return "";

  let hour = Number(match[1]);
  const minute = Number(match[2]);

  if (minute > 59) return "";

  if (match[3]) {
    const period = match[3].toUpperCase();

    if (hour < 1 || hour > 12) return "";
    if (period === "AM" && hour === 12) hour = 0;
    if (period === "PM" && hour !== 12) hour += 12;
  } else if (hour > 23) {
    return "";
  }

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function parseDateTime(value) {
  const clean = removeMatchId(cleanCell(value));

  const match = clean.match(
    /^(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+(.+)$/i
  );

  if (!match) return null;

  const datePart = match[1];
  const time = normaliseTime(match[2]);

  if (!time) return null;

  let date;

  if (/^today$/i.test(datePart)) {
    date = getEATDate(0);
  } else if (/^tomorrow$/i.test(datePart)) {
    date = getEATDate(1);
  } else if (/^\d{4}-/.test(datePart)) {
    const [year, month, day] = datePart.split("-").map(Number);

    date = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  } else {
    const [day, month, rawYear] = datePart.split("/").map(Number);
    const year = rawYear < 100 ? rawYear + 2000 : rawYear;

    date = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }

  if (!isValidDate(date)) return null;

  return { date, time };
}

function parseLeague(value) {
  const clean = cleanCell(value);
  const parts = clean.split(/\s+-\s+/);

  if (parts.length >= 2) {
    return {
      country: parts[0].trim(),
      league: parts.slice(1).join(" - ").trim(),
    };
  }

  // Baadhi ya bookmaker huonyesha kichwa kama "Czechia, Czech Liga Pro"
  // badala ya "Czechia - Czech Liga Pro".
  const commaParts = clean.split(/,\s*/);
  if (commaParts.length >= 2) {
    return {
      country: commaParts[0].trim(),
      league: commaParts.slice(1).join(", ").trim(),
    };
  }

  return {
    country: "Other",
    league: clean || "Unknown League",
  };
}

function isLeagueLine(value) {
  const clean = cleanCell(value);

  return (
    ( /^[^|]+\s+-\s+[^|]+$/.test(clean) ||
      (/^[^|,]+,\s*[^|,].+$/.test(clean) &&
       /\b(league|series|liga|cup|championship|open|tour|pro)\b/i.test(clean)) ) &&
    !/^(Today|Tomorrow)\b/i.test(clean) &&
    !/^\d{1,2}:\d{2}/.test(clean) &&
    !/^\+?\d+(?:\.\d+)?$/.test(clean)
  );
}

// Mfano: 1.392.70 -> 1.39 na 2.70.
function extractOdds(value) {
  return (
    String(value || "")
      .replace(/\s/g, "")
      .match(/\d+\.\d{2}/g) || []
  );
}

function parseOdds(value) {
  const prices = extractOdds(value);

  if (prices.length < 2) return null;

  const home = Number(prices[0]);
  const away = Number(prices[1]);

  if (
    !Number.isFinite(home) ||
    !Number.isFinite(away) ||
    home < 1.01 ||
    away < 1.01
  ) {
    return null;
  }

  return { home, away };
}

function isDateLine(value) {
  return /^(Today|Tomorrow|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})\s+/i.test(
    removeMatchId(value)
  );
}

function parseTableTennisText(rawText) {
  const lines = String(rawText || "")
    .replace(/\r/g, "")
    .replace(/\u00a0/g, " ")
    .replace(/&nbsp;/gi, " ")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const matches = [];

  let league = "";
  let pending = null;
  let playerBuffer = [];

  function savePending(dateTime = null) {
    if (!pending) return;

    if (dateTime) {
      pending.date = dateTime.date;
      pending.time = dateTime.time;
    }

    if (
      pending.homeTeam &&
      pending.awayTeam &&
      isValidDate(pending.date) &&
      normaliseTime(pending.time) &&
      pending.homeTeam.toLowerCase() !==
        pending.awayTeam.toLowerCase()
    ) {
      matches.push({
        ...pending,
        time: normaliseTime(pending.time),
      });
    }

    pending = null;
  }

  function startMatch(homeTeam, awayTeam, odds) {
    if (!league || !homeTeam || !awayTeam || !odds) return;

    const leagueInfo = parseLeague(league);

    pending = {
      country: leagueInfo.country,
      league: leagueInfo.league,
      homeTeam: cleanCell(homeTeam),
      awayTeam: cleanCell(awayTeam),
      date: "",
      time: "",
      homeOdds: odds.home,
      awayOdds: odds.away,
    };
  }

  for (const line of lines) {
    if (/^[\s|:\-]+$/.test(line)) continue;

    const rawCells = line.includes("|")
      ? line.replace(/^\|/, "").replace(/\|$/, "").split("|")
      : [line];

    const cells = rawCells.map(cleanCell).filter(Boolean);

    if (!cells.length) continue;

    // Format ya jedwali lako: | Czechia, Czech Liga Pro | 12 |
    // Hapa jina la ligi na nchi viko kwenye cell moja, na idadi ya mechi
    // iko kwenye cell inayofuata. Bila sharti hili parser haioni ligi.
    const commaLeagueHeader =
      cells.length >= 2 &&
      cells[0].includes(",") &&
      /^\d+$/.test(cells[1]) &&
      !cells.some((item) => extractOdds(item).length >= 2);

    if (commaLeagueHeader) {
      savePending();
      league = cells[0];
      playerBuffer = [];
      continue;
    }

    const leagueCell = cells.find(
      (cell) =>
        isLeagueLine(cell) &&
        !isDateLine(cell) &&
        !cells.some((item) => extractOdds(item).length >= 2)
    );

    if (leagueCell) {
      savePending();
      league = leagueCell;
      playerBuffer = [];
      continue;
    }

    if (!league) continue;

    const dateCell = cells.find((cell) => isDateLine(cell));

    if (dateCell) {
      const dateTime = parseDateTime(dateCell);

      if (dateTime) {
        savePending(dateTime);
        playerBuffer = [];
      }

      continue;
    }

    const oddsCell = cells.find(
      (cell) => extractOdds(cell).length >= 2
    );

    const names = cells.filter((cell) => {
      if (!cell || cell === oddsCell) return false;
      if (/^\+?\d+$/.test(cell)) return false;
      if (/^#?\d{4,}$/.test(cell)) return false;
      if (/^:?-{2,}:?$/.test(cell)) return false;
      if (/^[+-]?\d+(?:\.\d+)?$/.test(cell)) return false;
      if (isDateLine(cell)) return false;
      if (/^(Country|League|Home|Away|Odds)$/i.test(cell)) return false;

      return true;
    });

    if (oddsCell) {
      const odds = parseOdds(oddsCell);

      if (!odds) continue;

      const players = [...playerBuffer, ...names];

      if (players.length >= 2) {
        savePending();

        startMatch(
          players[players.length - 2],
          players[players.length - 1],
          odds
        );

        playerBuffer = [];
      }

      continue;
    }

    playerBuffer.push(...names);

    if (playerBuffer.length > 2) {
      playerBuffer = playerBuffer.slice(-2);
    }
  }

  savePending();

  return matches;
}

function calculateSets(score) {
  let homeSets = 0;
  let awaySets = 0;
  let playedSets = 0;
  let foundEmptySet = false;

  for (const setNumber of SETS) {
    const home = safeNumber(score[`homeSet${setNumber}`]);
    const away = safeNumber(score[`awaySet${setNumber}`]);

    if (home == null && away == null) {
      foundEmptySet = true;
      continue;
    }

    if (foundEmptySet) {
      return {
        valid: false,
        homeSets,
        awaySets,
        playedSets,
        winner: null,
      };
    }

    if (
      home == null ||
      away == null ||
      !Number.isInteger(home) ||
      !Number.isInteger(away) ||
      home < 0 ||
      away < 0 ||
      home === away
    ) {
      return {
        valid: false,
        homeSets,
        awaySets,
        playedSets,
        winner: null,
      };
    }

    playedSets++;

    if (home > away) homeSets++;
    else awaySets++;
  }

  return {
    valid: true,
    homeSets,
    awaySets,
    playedSets,
    winner:
      homeSets === awaySets
        ? null
        : homeSets > awaySets
        ? "home"
        : "away",
  };
}

function makeInitialScore(score = {}) {
  const result = {};

  for (const setNumber of SETS) {
    result[`homeSet${setNumber}`] =
      score[`homeSet${setNumber}`] ?? "";
    result[`awaySet${setNumber}`] =
      score[`awaySet${setNumber}`] ?? "";
  }

  return result;
}

function validateScoreOdds(scoreOdds) {
  const result = [];

  for (const item of scoreOdds) {
    const odds = safeNumber(item.odds);

    if (odds == null || odds < 1.01) {
      return {
        valid: false,
        message: `Weka odds sahihi kwa score ${item.score}; kiwango cha chini ni 1.01.`,
      };
    }

    result.push({
      score: item.score,
      odds,
    });
  }

  return {
    valid: true,
    data: result,
  };
}

function makeMatchDocument(match, homeOdds, awayOdds, scoreOdds) {
  return {
    sport: "tabletennis",
    country: match.country,
    league: match.league,
    homeTeam: match.homeTeam,
    awayTeam: match.awayTeam,
    A: match.homeTeam,
    B: match.awayTeam,
    date: match.date,
    time: normaliseTime(match.time),
    timezone: TIMEZONE,

    odds: {
      home: homeOdds,
      away: awayOdds,
      all: [homeOdds, awayOdds],
    },

    // Correct score odds za mwisho wa mechi.
    scoreOdds,

    // Hizi ni set/game scores za matokeo halisi ya mechi.
    score: {
      homeSet1: null,
      awaySet1: null,
      homeSet2: null,
      awaySet2: null,
      homeSet3: null,
      awaySet3: null,
      homeSet4: null,
      awaySet4: null,
      homeSet5: null,
      awaySet5: null,
      homeSets: 0,
      awaySets: 0,
      winner: null,
    },

    status: "upcoming",
    source: "admin-tabletennis",
    createdBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

export default function AdminAddTabletennes() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [score, setScore] = useState(EMPTY_SCORE);
  const [scoreOdds, setScoreOdds] = useState(makeDefaultScoreOdds);
  const [matches, setMatches] = useState([]);
  const [bulkText, setBulkText] = useState("");
  const [search, setSearch] = useState("");
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const preview = useMemo(
    () => parseTableTennisText(bulkText),
    [bulkText]
  );

  const liveSetResult = useMemo(
    () => calculateSets(score),
    [score]
  );

  async function loadMatches() {
    setLoading(true);
    setError("");

    try {
      const snapshot = await getDocs(collection(db, COLLECTION));

      const rows = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      rows.sort((a, b) =>
        `${a.date || ""} ${a.time || ""}`.localeCompare(
          `${b.date || ""} ${b.time || ""}`
        )
      );

      setMatches(rows);
    } catch (err) {
      setError(
        err?.message || "Imeshindikana kusoma collection ya table tennis."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMatches();
  }, []);

  function handleFormChange(event) {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }

  function handleScoreOddsChange(index, value) {
    setScoreOdds((current) =>
      current.map((item, i) =>
        i === index ? { ...item, odds: value } : item
      )
    );
  }

  async function addSingleMatch(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    if (
      !form.country.trim() ||
      !form.league.trim() ||
      !form.homeTeam.trim() ||
      !form.awayTeam.trim() ||
      !isValidDate(form.date) ||
      !normaliseTime(form.time)
    ) {
      setError("Jaza taarifa zote na hakikisha tarehe na muda ni sahihi.");
      return;
    }

    if (
      form.homeTeam.trim().toLowerCase() ===
      form.awayTeam.trim().toLowerCase()
    ) {
      setError("Majina ya wachezaji hayawezi kuwa sawa.");
      return;
    }

    const homeOdds = safeNumber(form.homeOdds);
    const awayOdds = safeNumber(form.awayOdds);

    if (
      homeOdds == null ||
      awayOdds == null ||
      homeOdds < 1.01 ||
      awayOdds < 1.01
    ) {
      setError("Weka odds za wachezaji wote wawili, angalau 1.01.");
      return;
    }

    const checkedOdds = validateScoreOdds(scoreOdds);

    if (!checkedOdds.valid) {
      setError(checkedOdds.message);
      return;
    }

    setSaving(true);

    try {
      const match = {
        country: form.country.trim(),
        league: form.league.trim(),
        homeTeam: form.homeTeam.trim(),
        awayTeam: form.awayTeam.trim(),
        date: form.date,
        time: form.time,
      };

      await addDoc(
        collection(db, COLLECTION),
        makeMatchDocument(
          match,
          homeOdds,
          awayOdds,
          checkedOdds.data
        )
      );

      setForm(EMPTY_FORM);
      setScoreOdds(makeDefaultScoreOdds());
      setSuccess("Mechi, match odds na correct score odds zimehifadhiwa.");
      await loadMatches();
    } catch (err) {
      setError(err?.message || "Imeshindikana kuhifadhi mechi.");
    } finally {
      setSaving(false);
    }
  }

  async function importBulkMatches(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    const parsed = parseTableTennisText(bulkText);

    if (!parsed.length) {
      setError(
        "Hakuna mechi zilizotambuliwa. Hakikisha ligi, wachezaji, odds na muda vipo."
      );
      return;
    }

    setBulkSaving(true);
    let saved = 0;

    try {
      for (const match of parsed) {
        const correctOdds = DEFAULT_SCORE_ODDS.map((item) => ({
          score: item.score,
          odds: item.odds,
        }));

        await addDoc(
          collection(db, COLLECTION),
          makeMatchDocument(
            match,
            match.homeOdds,
            match.awayOdds,
            correctOdds
          )
        );

        saved++;
      }

      setBulkText("");
      setSuccess(
        `Mechi ${saved} zimehifadhiwa pamoja na correct score odds.`
      );
      await loadMatches();
    } catch (err) {
      setError(
        `${saved} zimehifadhiwa kabla ya hitilafu. ${err?.message || ""}`
      );
      await loadMatches();
    } finally {
      setBulkSaving(false);
    }
  }

  function selectMatch(match) {
    setSelectedMatchId(match.id);
    setScore(makeInitialScore(match.score || {}));
    setScoreOdds(makeInitialScoreOdds(match.scoreOdds));
    setError("");
    setSuccess("");

    document.getElementById("att-score-editor")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  async function saveScore() {
    setError("");
    setSuccess("");

    if (!getAppLoginUser()) {
      setError("Tafadhali login kama admin kwanza.");
      return;
    }

    if (!selectedMatchId) {
      setError("Chagua mechi kwanza.");
      return;
    }

    const result = calculateSets(score);

    if (!result.valid) {
      setError(
        "Kagua seti: kila set iliyoingizwa iwe na alama za wachezaji wote wawili zisizolingana, bila kuacha seti katikati."
      );
      return;
    }

    // Best of 5: mshindi anahitaji games/sets 3.
    if (
      !result.winner ||
      Math.max(result.homeSets, result.awaySets) !== 3 ||
      result.playedSets > 5
    ) {
      setError(
        "Kwa Best of 5, mshindi lazima awe ameshinda games 3. Ingiza matokeo halisi ya kila game."
      );
      return;
    }

    const checkedOdds = validateScoreOdds(scoreOdds);

    if (!checkedOdds.valid) {
      setError(checkedOdds.message);
      return;
    }

    const savedScore = {};

    for (const setNumber of SETS) {
      savedScore[`homeSet${setNumber}`] = safeNumber(
        score[`homeSet${setNumber}`]
      );
      savedScore[`awaySet${setNumber}`] = safeNumber(
        score[`awaySet${setNumber}`]
      );
    }

    savedScore.homeSets = result.homeSets;
    savedScore.awaySets = result.awaySets;
    savedScore.winner = result.winner;

    const selected = matches.find(
      (match) => match.id === selectedMatchId
    );

    setSaving(true);

    try {
      await updateDoc(doc(db, COLLECTION, selectedMatchId), {
        score: savedScore,
        scoreOdds: checkedOdds.data,
        status: "finished",
        winner:
          result.winner === "home"
            ? selected?.homeTeam || selected?.A
            : selected?.awayTeam || selected?.B,
        updatedAt: serverTimestamp(),
        resultUpdatedBy: getAppLoginUser()?.uid || getAppLoginUser()?.username || "unknown",
        resultSavedAt: serverTimestamp(),
      });

      setSelectedMatchId("");
      setScore(EMPTY_SCORE);
      setScoreOdds(makeDefaultScoreOdds());
      setSuccess("Correct score odds na matokeo vimehifadhiwa.");
      await loadMatches();
    } catch (err) {
      setError(err?.message || "Imeshindikana kuhifadhi matokeo.");
    } finally {
      setSaving(false);
    }
  }

  const filteredMatches = matches.filter((match) =>
    [
      match.country,
      match.league,
      match.homeTeam,
      match.awayTeam,
      match.A,
      match.B,
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const selectedMatch = matches.find(
    (match) => match.id === selectedMatchId
  );

  return (
    <main className="admin-basketball-page">
      <header className="ab-header">
        <div>
          <span className="ab-eyebrow">ADMIN / SPORTS</span>
          <h1>Admin Add Table Tennis</h1>
          <p>Mechi, match odds, correct score odds na matokeo.</p>
        </div>

        <div className="ab-count">
          <strong>{matches.length}</strong>
          <span>Matches</span>
        </div>
      </header>

      {error && <div className="ab-alert ab-error">{error}</div>}
      {success && <div className="ab-alert ab-success">{success}</div>}

      <section className="ab-panel ab-bulk-panel">
        <div className="ab-panel-heading">
          <div>
            <h2>Paste / Import Mechi Nyingi</h2>
            <p>Bandika bookmaker table tennis hapa.</p>
          </div>
        </div>

        <form className="ab-form" onSubmit={importBulkMatches}>
          <label className="ab-correct-scores-field">
            Bandika bookmaker hapa
            <textarea
              className="ab-bulk-textarea"
              value={bulkText}
              onChange={(event) => setBulkText(event.target.value)}
              placeholder={`Czechia - Czech Liga Pro
Svoboda, Adam
Briza, Frantisek
1.392.70
Tomorrow 03:00#36408`}
              rows={12}
              required
            />
          </label>

          <div className="ab-form-actions">
            <button
              className="ab-primary"
              type="submit"
              disabled={bulkSaving || preview.length === 0}
            >
              {bulkSaving
                ? "Ina-import..."
                : `Import Mechi ${preview.length}`}
            </button>

            <button
              className="ab-secondary"
              type="button"
              onClick={() => setBulkText("")}
              disabled={bulkSaving}
            >
              Futa Orodha
            </button>
          </div>
        </form>

        <p className="ab-note">
          Bulk import itaweka correct score odds za mwanzo kwenye kila mechi.
        </p>

        <div className="ab-bulk-preview">
          <h3>Preview ({preview.length})</h3>

          {preview.length === 0 ? (
            <p className="ab-empty">
              Bandika orodha ya bookmaker ili kuona mechi hapa.
            </p>
          ) : (
            <div className="ab-match-list">
              {preview.map((match, index) => (
                <article
                  className="ab-match-card"
                  key={`${match.homeTeam}-${match.awayTeam}-${index}`}
                >
                  <div className="ab-match-meta">
                    <span>{match.country} · {match.league}</span>
                  </div>

                  <div className="ab-teams">
                    <div><strong>{match.homeTeam}</strong></div>
                    <span className="ab-vs">VS</span>
                    <div><strong>{match.awayTeam}</strong></div>
                  </div>

                  <div className="ab-match-foot">
                    <span>{match.date} · {match.time} EAT</span>
                    <span>
                      Match odds: {match.homeOdds} / {match.awayOdds}
                    </span>
                  </div>

                  <div className="ab-correct-score-box">
                    <strong>Correct Score Odds</strong>
                    <div className="ab-correct-score-grid">
                      {DEFAULT_SCORE_ODDS.map((item) => (
                        <span key={item.score}>
                          {item.score} @ {item.odds.toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="ab-panel">
        <div className="ab-panel-heading">
          <div>
            <h2>Ongeza Mechi Moja</h2>
            <p>Ingiza wachezaji na odds kwa mkono.</p>
          </div>
        </div>

        <form className="ab-form" onSubmit={addSingleMatch}>
          <label>
            Nchi
            <input
              name="country"
              value={form.country}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Ligi
            <input
              name="league"
              value={form.league}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Mchezaji wa kwanza
            <input
              name="homeTeam"
              value={form.homeTeam}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Mchezaji wa pili
            <input
              name="awayTeam"
              value={form.awayTeam}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Tarehe
            <input
              type="date"
              name="date"
              value={form.date}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Muda (EAT)
            <input
              type="time"
              name="time"
              value={form.time}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Odds za mchezaji wa kwanza
            <input
              type="number"
              min="1.01"
              step="0.01"
              name="homeOdds"
              value={form.homeOdds}
              onChange={handleFormChange}
              required
            />
          </label>

          <label>
            Odds za mchezaji wa pili
            <input
              type="number"
              min="1.01"
              step="0.01"
              name="awayOdds"
              value={form.awayOdds}
              onChange={handleFormChange}
              required
            />
          </label>

          <div className="ab-correct-score-box ab-correct-score-editor">
            <h3>Correct Score Odds — Best of 5</h3>
            <p>
              Weka odds za matokeo ya mwisho ya games. Kwa mfano, 3-1
              maana yake mchezaji wa kwanza ameshinda games 3 na wa pili 1.
            </p>

            <div className="ab-correct-score-grid">
              {scoreOdds.map((item, index) => (
                <label key={item.score}>
                  Score {item.score}
                  <input
                    type="number"
                    min="1.01"
                    step="0.01"
                    value={item.odds}
                    onChange={(event) =>
                      handleScoreOddsChange(index, event.target.value)
                    }
                    required
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="ab-form-actions">
            <button className="ab-primary" type="submit" disabled={saving}>
              {saving ? "Inahifadhi..." : "Hifadhi Mechi na Odds"}
            </button>

            <button
              className="ab-secondary"
              type="button"
              onClick={() => {
                setForm(EMPTY_FORM);
                setScoreOdds(makeDefaultScoreOdds());
              }}
            >
              Futa Form
            </button>
          </div>
        </form>
      </section>

      <section className="ab-panel" id="att-score-editor">
        <div className="ab-panel-heading">
          <div>
            <h2>Hariri Correct Score Odds na Matokeo</h2>
            <p>Chagua mechi kwenye orodha, kisha hifadhi mabadiliko.</p>
          </div>
        </div>

        {selectedMatch ? (
          <div className="ab-selected-match">
            <span>Mechi iliyochaguliwa</span>
            <strong>
              {selectedMatch.homeTeam || selectedMatch.A} VS{" "}
              {selectedMatch.awayTeam || selectedMatch.B}
            </strong>
          </div>
        ) : (
          <p className="ab-note">
            Chagua mechi kutoka kwenye orodha iliyo chini.
          </p>
        )}

        <div className="ab-correct-score-box ab-correct-score-editor">
          <h3>Correct Score Odds</h3>

          <div className="ab-correct-score-grid">
            {scoreOdds.map((item, index) => (
              <label key={item.score}>
                Score {item.score}
                <input
                  type="number"
                  min="1.01"
                  step="0.01"
                  value={item.odds}
                  onChange={(event) =>
                    handleScoreOddsChange(index, event.target.value)
                  }
                  disabled={!selectedMatchId || saving}
                  required
                />
              </label>
            ))}
          </div>
        </div>

        <div className="ab-quarter-grid">
          {SETS.map((setNumber) => (
            <div className="ab-quarter-card" key={setNumber}>
              <h4>Game {setNumber}</h4>

              <label>
                {selectedMatch?.homeTeam || "Mchezaji wa kwanza"}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={score[`homeSet${setNumber}`]}
                  onChange={(event) =>
                    setScore((current) => ({
                      ...current,
                      [`homeSet${setNumber}`]: event.target.value,
                    }))
                  }
                  disabled={!selectedMatchId || saving}
                />
              </label>

              <label>
                {selectedMatch?.awayTeam || "Mchezaji wa pili"}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={score[`awaySet${setNumber}`]}
                  onChange={(event) =>
                    setScore((current) => ({
                      ...current,
                      [`awaySet${setNumber}`]: event.target.value,
                    }))
                  }
                  disabled={!selectedMatchId || saving}
                />
              </label>
            </div>
          ))}
        </div>

        <div className="ab-selected-match">
          <span>Game result preview</span>
          <strong>
            {liveSetResult.homeSets} — {liveSetResult.awaySets}
          </strong>
          <span>
            {liveSetResult.winner === "home"
              ? selectedMatch?.homeTeam
              : liveSetResult.winner === "away"
              ? selectedMatch?.awayTeam
              : "Mshindi bado hajajulikana"}
          </span>
        </div>

        <div className="ab-form-actions">
          <button
            className="ab-primary"
            type="button"
            onClick={saveScore}
            disabled={!selectedMatchId || saving}
          >
            {saving ? "Inahifadhi..." : "Hifadhi Odds na Matokeo"}
          </button>

          <button
            className="ab-secondary"
            type="button"
            onClick={() => {
              setSelectedMatchId("");
              setScore(EMPTY_SCORE);
              setScoreOdds(makeDefaultScoreOdds());
            }}
          >
            Futa Score Form
          </button>
        </div>
      </section>

      <section className="ab-panel">
        <div className="ab-panel-heading ab-list-heading">
          <div>
            <h2>Mechi Zilizohifadhiwa</h2>
            <p>Mechi kutoka Firebase collection tabletennis.</p>
          </div>

          <button
            className="ab-secondary"
            type="button"
            onClick={loadMatches}
            disabled={loading}
          >
            {loading ? "Inapakia..." : "Refresh"}
          </button>
        </div>

        <input
          className="ab-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Tafuta ligi au mchezaji..."
        />

        {loading ? (
          <p className="ab-empty">Inapakia mechi...</p>
        ) : filteredMatches.length === 0 ? (
          <p className="ab-empty">Hakuna mechi zilizopatikana.</p>
        ) : (
          <div className="ab-match-list">
            {filteredMatches.map((match) => {
              const currentScore = match.score || {};
              const isSelected = selectedMatchId === match.id;
              const savedScoreOdds = Array.isArray(match.scoreOdds)
                ? match.scoreOdds
                : [];

              return (
                <article className="ab-match-card" key={match.id}>
                  <div className="ab-match-meta">
                    <span>{match.country || "—"}</span>
                    <span>{match.league || "—"}</span>
                    <span className={`ab-status ab-status-${match.status || "upcoming"}`}>
                      {match.status || "upcoming"}
                    </span>
                  </div>

                  <div className="ab-teams">
                    <div>
                      <strong>
                        {match.homeTeam || match.A || "Player 1"}
                      </strong>
                      <small>{currentScore.homeSets ?? 0} games</small>
                    </div>

                    <span className="ab-vs">VS</span>

                    <div>
                      <strong>
                        {match.awayTeam || match.B || "Player 2"}
                      </strong>
                      <small>{currentScore.awaySets ?? 0} games</small>
                    </div>
                  </div>

                  <div className="ab-match-foot">
                    <span>{match.date || "—"} · {match.time || "—"}</span>
                    <span>
                      Match odds: {match.odds?.home ?? "—"} /{" "}
                      {match.odds?.away ?? "—"}
                    </span>
                  </div>

                  <div className="ab-correct-score-box">
                    <strong>Correct Score Odds</strong>

                    {savedScoreOdds.length > 0 ? (
                      <div className="ab-correct-score-grid">
                        {savedScoreOdds.map((item) => (
                          <span key={item.score}>
                            {item.score} @ {Number(item.odds).toFixed(2)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="ab-note">
                        Hakuna correct score odds zilizohifadhiwa kwa mechi hii.
                        Chagua mechi ili kuweka odds.
                      </p>
                    )}
                  </div>

                  <div className="ab-match-foot">
                    <span>
                      {SETS.map((setNumber) => {
                        const home = currentScore[`homeSet${setNumber}`];
                        const away = currentScore[`awaySet${setNumber}`];

                        if (home == null && away == null) return null;

                        return `G${setNumber} ${home ?? "—"}-${away ?? "—"} `;
                      })}
                    </span>

                    {match.winner && <span>Mshindi: {match.winner}</span>}
                  </div>

                  <button
                    className={isSelected ? "ab-secondary" : "ab-primary"}
                    type="button"
                    onClick={() => selectMatch(match)}
                    disabled={saving}
                  >
                    {isSelected ? "Mechi Imechaguliwa" : "Hariri Odds / Ingiza Score"}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <p className="ab-footnote">
        Collection: <code>tabletennis</code>. Correct score odds zimehifadhiwa
        kwenye field <code>scoreOdds</code>.
      </p>
    </main>
  );
}
